import type { WType } from "@/lib/widget-types";

export type WidgetKind = "number" | "number_spark" | "line" | "bar" | "bar_horizontal" | "share" | "top" | "mini_table" | "compare" | "gauge" | "combo" | "alerts" | "funnel" | "timeline" | "special";

export type WidgetMetric =
  | "revenue" | "orders" | "returns" | "ad_spend" | "avg_check" | "buyout_rate"
  | "stock" | "revenue_orders" | "order_statuses" | "warehouses" | "revenue_share"
  | "alerts" | "period_compare" | "drr" | "ad_funnel" | "radar" | "scatter_revenue_orders" | "events";

export type WidgetBreakdown = "total" | "day" | "warehouse" | "status" | "product" | "period" | "goal" | "rules" | "funnel" | "score" | "weekday" | "average";

export type WidgetChoice = { metric: WidgetMetric; label: string; def: WType };
export type BuilderOption = {
  visual: WidgetKind;
  metric: WidgetMetric;
  metric2?: WidgetMetric;
  breakdown: WidgetBreakdown;
  def: WType;
  note: string;
  series?: string[];
};

export const KIND_LABELS: Record<WidgetKind, { title: string; desc: string }> = {
  number: { title: "Число", desc: "только значение" },
  number_spark: { title: "Число + график", desc: "одинаковая мини-линия" },
  line: { title: "Линия", desc: "один показатель по дням" },
  bar: { title: "Вертикальные столбцы", desc: "форма не зависит от показателя" },
  bar_horizontal: { title: "Горизонтальные столбцы", desc: "та же логика, другая форма" },
  share: { title: "Доля", desc: "структура и пропорции" },
  top: { title: "Топ", desc: "лидеры списка" },
  mini_table: { title: "Мини-таблица", desc: "несколько строк с цифрами" },
  compare: { title: "Сравнение", desc: "период к периоду" },
  gauge: { title: "Индикатор", desc: "процент или цель" },
  combo: { title: "Комбо", desc: "две или несколько серий" },
  alerts: { title: "Предупреждения", desc: "что требует внимания" },
  funnel: { title: "Воронка", desc: "путь по шагам" },
  timeline: { title: "События", desc: "что могло повлиять" },
  special: { title: "Особые", desc: "готовые сложные формы" },
};

export const KIND_ORDER: WidgetKind[] = ["number", "number_spark", "line", "bar", "bar_horizontal", "share", "top", "mini_table", "compare", "gauge", "combo", "alerts", "funnel", "timeline", "special"];

export const METRIC_LABELS: Record<WidgetMetric, string> = {
  revenue: "Выручка",
  orders: "Заказы",
  returns: "Возвраты",
  ad_spend: "Расход рекламы",
  avg_check: "Средний чек",
  buyout_rate: "% выкупа",
  stock: "Остатки",
  revenue_orders: "Выручка + заказы",
  order_statuses: "Статусы заказов",
  warehouses: "Склады",
  revenue_share: "Доля выручки",
  alerts: "Уведомления",
  period_compare: "Сравнение периодов",
  drr: "ДРР",
  ad_funnel: "Воронка рекламы",
  radar: "Радар здоровья",
  scatter_revenue_orders: "Заказы vs выручка",
  events: "События",
};

export const BREAKDOWN_LABELS: Record<WidgetBreakdown, string> = {
  total: "Итог",
  day: "По дням",
  warehouse: "По складам",
  status: "По статусам",
  product: "По товарам",
  period: "Период к периоду",
  goal: "К цели",
  rules: "По правилам",
  funnel: "По шагам",
  score: "Оценка 0-100",
  weekday: "По дням недели",
  average: "По дням + среднее",
};

export const BUILDER_OPTIONS: BuilderOption[] = [
  { visual: "number", metric: "revenue", breakdown: "total", def: "kpi_revenue", note: "итоговая выручка за 30 дней" },
  { visual: "number", metric: "orders", breakdown: "total", def: "kpi_orders", note: "итоговые заказы за 30 дней" },
  { visual: "number", metric: "returns", breakdown: "total", def: "kpi_returns", note: "возвраты за 30 дней" },
  { visual: "number", metric: "ad_spend", breakdown: "total", def: "kpi_adspend", note: "расход рекламы за 30 дней" },
  { visual: "number", metric: "avg_check", breakdown: "total", def: "kpi_avgcheck", note: "средний чек" },
  { visual: "number", metric: "buyout_rate", breakdown: "total", def: "kpi_buyout", note: "процент выкупа" },
  { visual: "number", metric: "stock", breakdown: "total", def: "chart_stock", note: "общий остаток" },
  { visual: "number", metric: "drr", breakdown: "total", def: "gauge_drr", note: "ДРР за период" },
  { visual: "number_spark", metric: "revenue", breakdown: "day", def: "kpi_revenue", note: "число и одинаковая мини-линия" },
  { visual: "number_spark", metric: "orders", breakdown: "day", def: "kpi_orders", note: "число и одинаковая мини-линия" },
  { visual: "number_spark", metric: "returns", breakdown: "day", def: "kpi_returns", note: "число и одинаковая мини-линия" },
  { visual: "number_spark", metric: "stock", breakdown: "day", def: "chart_stock", note: "число и одинаковая мини-линия" },
  { visual: "line", metric: "revenue", breakdown: "day", def: "chart_revenue", note: "выручка по дням" },
  { visual: "line", metric: "orders", breakdown: "day", def: "chart_orders", note: "заказы по дням" },
  { visual: "line", metric: "returns", breakdown: "day", def: "kpi_returns", note: "возвраты по дням" },
  { visual: "line", metric: "stock", breakdown: "day", def: "chart_stock", note: "расчётная динамика остатков" },
  { visual: "bar", metric: "orders", breakdown: "day", def: "bar_orders", note: "вертикальные столбцы заказов по дням" },
  { visual: "bar", metric: "revenue", breakdown: "day", def: "chart_revenue", note: "вертикальные столбцы выручки по дням" },
  { visual: "bar", metric: "returns", breakdown: "day", def: "kpi_returns", note: "вертикальные столбцы возвратов по дням" },
  { visual: "bar", metric: "stock", breakdown: "day", def: "chart_stock", note: "вертикальные столбцы остатков по дням" },
  { visual: "bar", metric: "order_statuses", breakdown: "status", def: "stacked_bar", note: "выкупы, отмены и возвраты вертикальными столбцами", series: ["Выкупы", "Отмены", "Возвраты"] },
  { visual: "bar", metric: "stock", breakdown: "warehouse", def: "warehouses", note: "остатки по складам вертикальными столбцами" },
  { visual: "bar", metric: "revenue", breakdown: "product", def: "top5_revenue", note: "топ товаров по выручке вертикальными столбцами" },
  { visual: "bar_horizontal", metric: "orders", breakdown: "day", def: "bar_orders", note: "горизонтальные столбцы заказов по дням" },
  { visual: "bar_horizontal", metric: "revenue", breakdown: "product", def: "top5_revenue", note: "товары по выручке горизонтальными столбцами" },
  { visual: "bar_horizontal", metric: "stock", breakdown: "warehouse", def: "horiz_bar", note: "остатки по складам горизонтальными столбцами" },
  { visual: "bar_horizontal", metric: "order_statuses", breakdown: "status", def: "horiz_bar", note: "итоги статусов горизонтальными столбцами", series: ["Выкупы", "Отмены", "Возвраты"] },
  { visual: "share", metric: "order_statuses", breakdown: "status", def: "donut_orders", note: "доли выкупов, отмен и возвратов", series: ["Выкупы", "Отмены", "Возвраты"] },
  { visual: "share", metric: "revenue", breakdown: "product", def: "donut_orders", note: "доля товаров в выручке" },
  { visual: "share", metric: "stock", breakdown: "warehouse", def: "donut_orders", note: "доля складов в остатках" },
  { visual: "top", metric: "revenue", breakdown: "product", def: "top5_revenue", note: "товары по выручке" },
  { visual: "top", metric: "stock", breakdown: "warehouse", def: "warehouses", note: "склады по остаткам" },
  { visual: "top", metric: "order_statuses", breakdown: "status", def: "top5_revenue", note: "статусы заказов списком" },
  { visual: "mini_table", metric: "revenue", breakdown: "product", def: "top5_revenue", note: "таблица товаров и выручки" },
  { visual: "mini_table", metric: "stock", breakdown: "warehouse", def: "warehouses", note: "таблица складов и остатков" },
  { visual: "mini_table", metric: "order_statuses", breakdown: "status", def: "top5_revenue", note: "таблица статусов заказов" },
  { visual: "compare", metric: "revenue", breakdown: "period", def: "compare_revenue", note: "последние 15 дней против предыдущих 15" },
  { visual: "compare", metric: "orders", breakdown: "period", def: "trend_orders", note: "последние 15 дней против предыдущих 15" },
  { visual: "compare", metric: "returns", breakdown: "period", def: "trend_orders", note: "последние 15 дней против предыдущих 15" },
  { visual: "compare", metric: "stock", breakdown: "period", def: "trend_orders", note: "последние 15 дней против предыдущих 15" },
  { visual: "gauge", metric: "buyout_rate", breakdown: "goal", def: "gauge_buyout", note: "процент выкупа к цели" },
  { visual: "gauge", metric: "drr", breakdown: "goal", def: "gauge_drr", note: "ДРР к цели" },
  { visual: "combo", metric: "revenue", metric2: "orders", breakdown: "day", def: "chart_combo", note: "две серии на одном графике", series: ["Выручка", "Заказы"] },
  { visual: "combo", metric: "order_statuses", breakdown: "day", def: "stacked_bar", note: "три серии статусов по дням", series: ["Выкупы", "Отмены", "Возвраты"] },
  { visual: "alerts", metric: "alerts", breakdown: "rules", def: "alerts", note: "автоматические предупреждения по магазину" },
  { visual: "funnel", metric: "ad_funnel", breakdown: "funnel", def: "funnel", note: "показы, клики, корзина, заказы", series: ["Показы", "Клики", "Корзина", "Заказы"] },
  { visual: "timeline", metric: "events", breakdown: "rules", def: "alerts", note: "лента событий и возможных причин" },
  { visual: "special", metric: "ad_funnel", breakdown: "funnel", def: "funnel", note: "показы, клики, корзина, заказы", series: ["Показы", "Клики", "Корзина", "Заказы"] },
  { visual: "special", metric: "scatter_revenue_orders", breakdown: "product", def: "scatter_rev_ord", note: "товары на поле заказы/выручка" },
  { visual: "special", metric: "revenue", breakdown: "average", def: "area_refline", note: "выручка по дням и средняя линия" },
];

export const WIDGET_CHOICES: Record<WidgetKind, WidgetChoice[]> = {
  number: [
    { metric: "revenue", label: "Выручка", def: "kpi_revenue" },
    { metric: "orders", label: "Заказы", def: "kpi_orders" },
    { metric: "returns", label: "Возвраты", def: "kpi_returns" },
    { metric: "ad_spend", label: "Расход рекламы", def: "kpi_adspend" },
    { metric: "avg_check", label: "Средний чек", def: "kpi_avgcheck" },
    { metric: "buyout_rate", label: "% выкупа", def: "kpi_buyout" },
  ],
  number_spark: [
    { metric: "revenue", label: "Выручка", def: "kpi_revenue" },
    { metric: "orders", label: "Заказы", def: "kpi_orders" },
    { metric: "returns", label: "Возвраты", def: "kpi_returns" },
  ],
  line: [
    { metric: "revenue", label: "Выручка", def: "chart_revenue" },
    { metric: "orders", label: "Заказы", def: "chart_orders" },
    { metric: "stock", label: "Расчёт остатков", def: "chart_stock" },
  ],
  bar: [
    { metric: "orders", label: "Заказы", def: "bar_orders" },
    { metric: "order_statuses", label: "Статусы по дням", def: "stacked_bar" },
    { metric: "warehouses", label: "Остатки по складам", def: "warehouses" },
    { metric: "warehouses", label: "Остатки горизонтально", def: "horiz_bar" },
  ],
  bar_horizontal: [
    { metric: "orders", label: "Заказы", def: "bar_orders" },
    { metric: "revenue", label: "Товары по выручке", def: "top5_revenue" },
    { metric: "warehouses", label: "Остатки по складам", def: "horiz_bar" },
    { metric: "order_statuses", label: "Статусы", def: "horiz_bar" },
  ],
  share: [
    { metric: "order_statuses", label: "Кольцо статусов", def: "donut_orders" },
    { metric: "order_statuses", label: "Круг статусов", def: "pie_full" },
    { metric: "revenue_share", label: "Доля выручки товаров", def: "treemap_revenue" },
  ],
  top: [
    { metric: "revenue", label: "Топ-5 по выручке", def: "top5_revenue" },
    { metric: "orders", label: "Тепловая карта заказов", def: "heatmap" },
    { metric: "alerts", label: "Уведомления", def: "alerts" },
  ],
  mini_table: [
    { metric: "revenue", label: "Товары по выручке", def: "top5_revenue" },
    { metric: "warehouses", label: "Остатки по складам", def: "warehouses" },
    { metric: "order_statuses", label: "Статусы", def: "top5_revenue" },
  ],
  compare: [
    { metric: "revenue", label: "Сравнение выручки", def: "compare_revenue" },
    { metric: "orders", label: "Тренд заказов", def: "trend_orders" },
    { metric: "revenue", label: "Тренд выручки", def: "trend_revenue" },
    { metric: "period_compare", label: "Сравнение периодов", def: "multi_line" },
  ],
  gauge: [
    { metric: "buyout_rate", label: "% выкупа", def: "gauge_buyout" },
    { metric: "drr", label: "ДРР", def: "gauge_drr" },
    { metric: "buyout_rate", label: "Радиальные показатели", def: "radial_rates" },
  ],
  combo: [
    { metric: "revenue_orders", label: "Выручка + заказы", def: "chart_combo" },
    { metric: "period_compare", label: "Сравнение периодов", def: "multi_line" },
    { metric: "order_statuses", label: "Статусы по дням", def: "stacked_bar" },
    { metric: "radar", label: "Радар здоровья", def: "radar_metrics" },
  ],
  alerts: [
    { metric: "alerts", label: "Предупреждения", def: "alerts" },
  ],
  funnel: [
    { metric: "ad_funnel", label: "Воронка рекламы", def: "funnel" },
  ],
  timeline: [
    { metric: "events", label: "События", def: "alerts" },
  ],
  special: [
    { metric: "ad_funnel", label: "Воронка рекламы", def: "funnel" },
    { metric: "scatter_revenue_orders", label: "Заказы vs выручка", def: "scatter_rev_ord" },
    { metric: "revenue", label: "Выручка + среднее", def: "area_refline" },
  ],
};

export const WIDGET_KIND_BY_DEF = KIND_ORDER.reduce((map, kind) => {
  for (const choice of WIDGET_CHOICES[kind]) {
    if (!map.has(choice.def)) map.set(choice.def, kind);
  }
  return map;
}, new Map<WType, WidgetKind>());

([
  ["top5_revenue", "top"],
  ["horiz_bar", "bar_horizontal"],
  ["warehouses", "bar_horizontal"],
  ["stacked_bar", "combo"],
  ["donut_orders", "share"],
  ["alerts", "alerts"],
  ["funnel", "funnel"],
] as [WType, WidgetKind][]).forEach(([def, kind]) => WIDGET_KIND_BY_DEF.set(def, kind));

export const WIDGET_CHOICE_BY_DEF = new Map<WType, WidgetChoice>(
  KIND_ORDER.flatMap(kind => WIDGET_CHOICES[kind].map(choice => [choice.def, choice] as const))
);

export const GROUP_LABEL_BY_DEF: Partial<Record<WType, string>> = {
  kpi_revenue: "Итог за 30 дней",
  kpi_orders: "Итог за 30 дней",
  kpi_returns: "Итог за 30 дней",
  kpi_adspend: "Итог за 30 дней",
  kpi_avgcheck: "Среднее за 30 дней",
  kpi_buyout: "Процент за 30 дней",
  chart_revenue: "По дням",
  chart_orders: "По дням",
  chart_stock: "Расчёт по дням",
  chart_combo: "По дням, 2 шкалы",
  bar_orders: "По дням",
  stacked_bar: "По дням и статусам",
  warehouses: "По складам",
  horiz_bar: "По складам",
  donut_orders: "По статусам",
  pie_full: "По статусам",
  treemap_revenue: "По товарам",
  top5_revenue: "По товарам",
  heatmap: "По дням недели",
  alerts: "Автоматические правила",
  compare_revenue: "15 дней к 15 дням",
  trend_orders: "7 дней к 7 дням",
  trend_revenue: "7 дней к 7 дням",
  multi_line: "Период к периоду",
  gauge_buyout: "Процент к цели",
  gauge_drr: "Процент к цели",
  radial_rates: "Несколько процентов",
  funnel: "По шагам рекламы",
  radar_metrics: "Оценка 0-100",
  scatter_rev_ord: "По товарам",
  area_refline: "По дням + среднее",
};

export function builderOptionsFor(visual: WidgetKind) {
  return BUILDER_OPTIONS.filter(option => option.visual === visual);
}

export function builderOption(visual: WidgetKind, metric: WidgetMetric, breakdown: WidgetBreakdown, metric2?: WidgetMetric) {
  return BUILDER_OPTIONS.find(option =>
    option.visual === visual &&
    option.metric === metric &&
    option.breakdown === breakdown &&
    (!option.metric2 || !metric2 || option.metric2 === metric2)
  );
}

export function builderOptionByDef(def: WType, visual?: WidgetKind) {
  return BUILDER_OPTIONS.find(option => option.def === def && (!visual || option.visual === visual)) ?? BUILDER_OPTIONS.find(option => option.def === def);
}
